import { useParams, Link, useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SchemaOrg from '@/components/feature/SchemaOrg';
import { categories } from '@/mocks/categories';
import { articles, getArticlesByCategory } from '@/mocks/articles';
import { articleThumbnails } from '@/mocks/thumbnails';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const currentCategory = categories.find((c) => c.id === slug);

  const allCategoryArticles = slug ? getArticlesByCategory(slug) : articles;
  const activeArticles = slug ? allCategoryArticles : articles;

  const handleCategoryChange = (categoryId: string) => {
    navigate(`/category/${categoryId}`);
  };

  const breadcrumbItems = [
    { label: t('breadcrumb.home'), to: '/' },
    { label: t('breadcrumb.categories'), to: '/category/pingtai-jieshao' },
    { label: currentCategory ? currentCategory.name : '全部分类' },
  ];

  const schemaData = {
    WebPage: {
      name: currentCategory ? `${currentCategory.name} - 金宝博文章分类` : '全部分类 - 金宝博',
      description: currentCategory
        ? `浏览金宝博${currentCategory.name}分类下的所有文章，了解金宝博平台的${currentCategory.name}相关内容。`
        : '浏览金宝博全部分类文章，了解金宝博平台的核心服务、使用指南与最新动态。',
      url: `https://www.jinbaobo.com/category/${slug || ''}`,
    },
    BreadcrumbList: {
      itemListElement: breadcrumbItems.map((item, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: item.label,
        item: item.to ? `https://www.jinbaobo.com${item.to}` : undefined,
      })),
    },
  };

  return (
    <>
      <ScrollToTop />
      <SchemaOrg type="WebPage" data={schemaData.WebPage} />
      <SchemaOrg type="BreadcrumbList" data={schemaData.BreadcrumbList} />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 md:pt-24 pb-14 md:pb-20">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb items={breadcrumbItems} />

            <div className="mt-6">
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-2">
                {currentCategory ? currentCategory.name : '全部分类'}
              </h1>
              <p className="text-sm text-foreground-500">
                {currentCategory
                  ? currentCategory.description
                  : '按主题探索金宝博的丰富内容'}
              </p>
            </div>

            <div className="mt-8 flex items-center gap-2 flex-wrap">
              <button
                onClick={() => navigate('/category/pingtai-jieshao')}
                className={`px-4 py-1.5 rounded-full text-xs font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  !slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                全部
              </button>
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => handleCategoryChange(cat.id)}
                  className={`px-4 py-1.5 rounded-full text-xs font-medium transition-colors whitespace-nowrap cursor-pointer ${
                    slug === cat.id
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {cat.name}
                </button>
              ))}
            </div>

            <div className="mt-8">
              {activeArticles.length > 0 ? (
                <>
                  <p className="text-xs text-foreground-400 mb-5">
                    共 {activeArticles.length} 篇文章
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                    {activeArticles.map((article) => (
                      <Link
                        key={article.id}
                        to={`/article/${article.id}`}
                        className="group bg-background-100 rounded-lg overflow-hidden hover:bg-primary-50/30 transition-colors cursor-pointer"
                      >
                        <div className="h-48 overflow-hidden">
                          <img
                            src={articleThumbnails[article.id]}
                            alt={article.title}
                            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                          />
                        </div>
                        <div className="p-5">
                          <div className="flex items-center gap-3 text-xs text-foreground-400 mb-2 flex-wrap">
                            <span>{article.date}</span>
                            <span>·</span>
                            <span>{article.readTime}</span>
                          </div>
                          <h2 className="font-heading text-sm font-semibold text-foreground-900 mb-2 group-hover:text-primary-500 transition-colors line-clamp-2">
                            {article.title}
                          </h2>
                          <p className="text-xs text-foreground-500 leading-relaxed line-clamp-2">
                            {article.excerpt}
                          </p>
                        </div>
                      </Link>
                    ))}
                  </div>
                </>
              ) : (
                <div className="text-center py-20">
                  <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                    <i className="ri-file-list-3-line text-2xl text-foreground-400"></i>
                  </div>
                  <p className="text-sm text-foreground-500">该分类暂无文章</p>
                </div>
              )}
            </div>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}